/** Aligné sur le backend : basic / growth / leader. */
export const BASIC = 'basic';
export const GROWTH = 'growth';
export const LEADER = 'leader';

/** Ligne UI « annuler l’abonnement Stripe » (pas un palier API). */
export const CANCEL_SELECT = '__cancel__';

export const PLAN_ORDER = {
  [CANCEL_SELECT]: 0,
  [BASIC]: 1,
  [GROWTH]: 2,
  [LEADER]: 3,
};

/** Aligné sur le backend : `month` | `year` pour l’UI (évite mensuel / annuel / yearly mélangés). */
export function normalizeBillingPeriod(raw) {
  const v = String(raw || '')
    .toLowerCase()
    .trim();
  if (
    ['year', 'yearly', 'annual', 'annuel', 'annuelle', 'y', '1y'].includes(v)
  ) {
    return 'year';
  }
  if (
    ['month', 'monthly', 'mensuel', 'mensuelle', 'm', '1m'].includes(v)
  ) {
    return 'month';
  }
  return 'month';
}

export function normalizeClientPlan(raw) {
  const v = String(raw || '')
    .toLowerCase()
    .trim();
  if (!v) return BASIC;
  if (
    [
      'tier0',
      'tier1',
      'default',
      'defaultplan',
      'free',
      'basic',
      'starter',
      '1',
    ].includes(v)
  ) {
    return BASIC;
  }
  if (['tier2', '2', 'growth', 'pro'].includes(v)) return GROWTH;
  if (['tier3', '3', 'leader', 'enterprise'].includes(v)) return LEADER;
  return BASIC;
}

/** Paiement crypto SYL (annuel) : Growth & Leader uniquement. */
export function isCryptoPaymentEligiblePlan(raw) {
  const p = normalizeClientPlan(raw);
  return p === GROWTH || p === LEADER;
}

export function planOrder(p) {
  const k = p === CANCEL_SELECT ? CANCEL_SELECT : normalizeClientPlan(p);
  return Object.prototype.hasOwnProperty.call(PLAN_ORDER, k) ? PLAN_ORDER[k] : 0;
}

/** Downgrade Stripe planifié (scheduled_price_id + palier cible plus bas). */
export function isScheduledPlanDowngrade(subscription) {
  if (!subscription?.scheduled_price_id) return false;
  const rawSched = subscription.scheduled_plan ?? subscription.scheduled_tier;
  if (rawSched == null || rawSched === '') return false;
  const current = normalizeClientPlan(subscription.plan ?? subscription.tier);
  const sched = normalizeClientPlan(rawSched);
  return planOrder(sched) < planOrder(current);
}

function parseDateSafe(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Essai Growth actif (aligné backend offerPolicyResolver). */
export function isGrowthTrialActive(subscription = {}) {
  if (!subscription || typeof subscription !== 'object') return false;
  const trialStatus = String(subscription.trial_status || 'active')
    .toLowerCase()
    .trim();
  if (trialStatus && trialStatus !== 'active') return false;
  const trialEndDate = parseDateSafe(subscription.trial_growth_ends_at);
  if (!trialEndDate) return false;
  return trialEndDate.getTime() > Date.now();
}

/** Abonnement Stripe actif et à jour. */
export function isStripeEntitled(subscription = {}) {
  if (!subscription || subscription.type !== 'stripe') return false;
  if (subscription.isSubscribed !== true) return false;
  const invoiceStatus = String(subscription.stripeInvoiceStatus || '')
    .toLowerCase()
    .trim();
  if (['payment_failed', 'past_due', 'unpaid'].includes(invoiceStatus)) {
    return false;
  }
  return true;
}

/** Palier effectif pour quotas UI (aligné backend resolveEntitledPlanCode). */
export function resolveEntitledClientPlan(subscription = {}) {
  let effectivePlan = BASIC;

  if (isGrowthTrialActive(subscription)) {
    effectivePlan = GROWTH;
  }

  if (isStripeEntitled(subscription)) {
    effectivePlan = normalizeClientPlan(
      subscription.plan ?? subscription.tier
    );
  }

  const grant = subscription?.accessGrant || null;
  if (grant?.plan && grant?.expiresAt) {
    const grantEnd = parseDateSafe(grant.expiresAt);
    if (grantEnd && grantEnd.getTime() > Date.now()) {
      const grantedPlan = normalizeClientPlan(grant.plan);
      if (planOrder(grantedPlan) > planOrder(effectivePlan)) {
        effectivePlan = grantedPlan;
      }
    }
  }

  return effectivePlan;
}
